import { localize } from "render/context/InternationalizationContext";

const AdditionalWorries = {
  Question: localize({
    en_GB: "Is there something you are worried about?",
    sv_SE: "Är det något du är orolig över?",
  }),
};

const Allergy = {
  Question: localize({
    en_GB: "What are you allergic to?",
    sv_SE: "Vad är du allergisk mot?",
  }),
  AnyAllergies: localize({
    en_GB: "Do you have any allergies?",
    sv_SE: "Har du några allergier?",
  }),
  Dairy: localize({
    en_GB: <>Dairy</>,
    sv_SE: <>Mejeriprodukter</>,
  }),
  InsectSting: localize({
    en_GB: <>Insect Sting</>,
    sv_SE: <>Insektsbett</>,
  }),
  Latex: localize({
    en_GB: <>Latex</>,
    sv_SE: <>Latex</>,
  }),
  Morphine: localize({
    en_GB: <>Morphine</>,
    sv_SE: <>Morfin</>,
  }),
  Nuts: localize({
    en_GB: <>Nuts</>,
    sv_SE: <>Nötter</>,
  }),
  Penicillin: localize({
    en_GB: <>Penicillin</>,
    sv_SE: <>Penicillin</>,
  }),
  Wheat: localize({
    en_GB: <>Wheat</>,
    sv_SE: <>Vete</>,
  }),
  Other: localize({
    en_GB: <>Other</>,
    sv_SE: <>Annat</>,
  }),
};

const Choice = {
  LessThanSixMonths: localize({
    en_GB: "Less than 6 months ago",
    sv_SE: "Mindre än 6 månader sedan",
  }),
  SixMonths: localize({
    en_GB: "More than 6 months ago",
    sv_SE: "Mer än 6 månader sedan",
  }),
};

const Diet = {
  Question: localize({
    en_GB: "What do your eating habits look like?",
    sv_SE: "Hur ser dina kostvanor ut?",
  }),
  Specific: localize({
    en_GB: <>To start, do you have any specific dietary habits?</>,
    sv_SE: <>Första frågan, har du några specifika kostvanor?</>,
  }),
  Vegan: localize({
    en_GB: <>Vegan</>,
    sv_SE: <>Vegan</>,
  }),
  Vegetarian: localize({
    en_GB: <>Vegetarian</>,
    sv_SE: <>Vegetarian</>,
  }),
  Pescatarian: localize({
    en_GB: <>Pescatarian</>,
    sv_SE: <>Pescatarian</>,
  }),
  Keto: localize({
    en_GB: <>Keto</>,
    sv_SE: <>Keto</>,
  }),
  Paleo: localize({
    en_GB: <>Paleo</>,
    sv_SE: <>Paleo</>,
  }),
  LowCarb: localize({
    en_GB: <>Low carb</>,
    sv_SE: <>Lågkalori</>,
  }),
  LowMeat: localize({
    en_GB: <>Low meat</>,
    sv_SE: <>Lågt kött intag</>,
  }),
  Other: localize({
    en_GB: <>Other</>,
    sv_SE: <>Annat</>,
  }),
  None: localize({
    en_GB: "None",
    sv_SE: "Nej",
  }),
  More: localize({
    en_GB: "More options",
    sv_SE: "Fler alternativ",
  }),
  LowCarbHighFat: localize({
    en_GB: "Low carb, High fat [LCHF]",
    sv_SE: "LCHF",
  }),
};

const Drinking = {
  Question: localize({
    en_GB: "How many glasses of alcohol do you drink a week?",
    sv_SE: "Hur många glas dricker du i veckan?",
  }),
  DoYouDrinkAlcohol: localize({
    en_GB: "Do you drink alcohol?",
    sv_SE: "Dricker du någon alkohol?",
  }),
  Yes: localize({
    en_GB: "Yes",
    sv_SE: "Ja",
  }),
  No: localize({
    en_GB: "No",
    sv_SE: "Nej",
  }),
  GlassesLessThanOne: localize({
    en_GB: "Less than 1 glass",
    sv_SE: "Mindre än ett glas",
  }),
  GlassesOneToTwo: localize({
    en_GB: "1-2 glasses",
    sv_SE: "1-2 glas",
  }),
  GlassesThreeToFour: localize({
    en_GB: "3-4 glasses",
    sv_SE: "3-4 glas",
  }),
  GlassesFiveToSix: localize({
    en_GB: "5-6 glasses",
    sv_SE: "5-6 glas",
  }),
  GlassesSevenToEight: localize({
    en_GB: "7-8 glasses",
    sv_SE: "7-8 glas",
  }),
  GlassesMoreThanNine: localize({
    en_GB: "More than 9 glasses",
    sv_SE: "Mer än 9 glas",
  }),
};

const HoursPerWeek = localize<{ hours: string }>({
  en_GB: ({ hours }) => `${hours} hours per week`,
  sv_SE: ({ hours }) => `${hours} timmar i veckan`,
});

const StrengthExercise = {
  Question: localize({
    en_GB: "How much strength exercise do you get per week?",
    sv_SE: "Hur ofta styrketränar du per vecka?",
  }),
  MoreThanPerWeek: localize<{ qty: string }>({
    en_GB: ({ qty }) => `More than ${qty} hours`,
    sv_SE: ({ qty }) => `Mer än ${qty} timmar`,
  }),
  HoursPerWeek,
};

const CardioExercise = {
  Question: localize({
    en_GB: "How much cardio exercise do you get per week?",
    sv_SE: "Hur ofta konditionstränar du per vecka?",
  }),
  MoreThanPerWeek: localize<{ qty: string }>({
    en_GB: ({ qty }) => `More than ${qty} hours`,
    sv_SE: ({ qty }) => `Mer än ${qty} timmar`,
  }),
  HoursPerWeek,
};

const FamilyConditions = {
  Question: localize({
    en_GB: "Do you have any diseases in the family?",
    sv_SE: "Har du några sjukdomar i släkten?",
  }),
};

const Heritage = {
  Question: localize({
    en_GB: "What's your ethnicity?",
    // TODO: update SE
    sv_SE: "Vart kommer din familj från?",
  }),
};

const WorldRegion = {
  Europe: localize({
    en_GB: <>Europe</>,
    sv_SE: <>Europa</>,
  }),
  NorthAmerica: localize({
    en_GB: <>North America</>,
    sv_SE: <>Nordamerika</>,
  }),
  CentralAmerica: localize({
    en_GB: <>Central America</>,
    sv_SE: <>Centralamerika</>,
  }),
  SouthAmerica: localize({
    en_GB: <>South America</>,
    sv_SE: <>Sydamerika</>,
  }),
  NorthAsia: localize({
    en_GB: <>North Asia</>,
    sv_SE: <>Norra Asien</>,
  }),
  MiddleEast: localize({
    en_GB: <>Middle East</>,
    sv_SE: <>Mellanöstern</>,
  }),
  EastAsia: localize({
    en_GB: <>East Asia</>,
    sv_SE: <>Östra Asien</>,
  }),
  SouthAsia: localize({
    en_GB: <>South Asia</>,
    sv_SE: <>Södra Asien</>,
  }),
  SouthEastAsia: localize({
    en_GB: <>Southeast Asia</>,
    sv_SE: <>Sydöstra Asien</>,
  }),
  Oceania: localize({
    en_GB: <>Oceania</>,
    sv_SE: <>Oceanien</>,
  }),
  NorthAfrica: localize({
    en_GB: <>North Africa</>,
    sv_SE: <>Nordafrika</>,
  }),
  WestAfrica: localize({
    en_GB: <>West Africa</>,
    sv_SE: <>Västafrika</>,
  }),
  EastAfrica: localize({
    en_GB: <>East Africa</>,
    sv_SE: <>Östafrika</>,
  }),
  SouthAfrica: localize({
    en_GB: <>South Africa</>,
    sv_SE: <>Sydafrika</>,
  }),
};

const Medication = {
  Question: localize({
    en_GB: "Do you take any medications?",
    sv_SE: "Tar du några mediciner?",
  }),
};

const PersonalConditions = {
  Question: localize({
    en_GB: "Have you had any health problems in the past year?",
    sv_SE: "Har du haft några problem med hälsan det senaste året?",
  }),
};

const Smoking = {
  Question: localize({
    en_GB: "Do you smoke cigarettes?",
    sv_SE: "Röker du cigaretter?",
  }),
  Yes: localize({
    en_GB: "Yes",
    sv_SE: "Ja",
  }),
  No: localize({
    en_GB: "No",
    sv_SE: "Nej",
  }),
  Quit: localize({
    en_GB: "No, I quit",
    sv_SE: "Nej, jag har slutat",
  }),
  DailyCigaretteCount: localize({
    en_GB: "How much do you smoke a day?",
    sv_SE: "Hur mycket röker du per dag?",
  }),
  CigaretteCountLessThanOne: localize({
    en_GB: "Less than 1 cigarette",
    sv_SE: "Mindre än en cigarett",
  }),
  CigaretteCountLessThanHalfAPack: localize({
    en_GB: "Less than half a pack",
    sv_SE: "Mindre än ett halvt paket",
  }),
  CigaretteCountHalfAPackOrMore: localize({
    en_GB: "Half a pack or more",
    sv_SE: "Ett halvt paket eller mer",
  }),
  CigaretteCountOnePackOrMore: localize({
    en_GB: "One pack or more",
    sv_SE: "Ett paket eller mer",
  }),
  QuitSmokingRecency: localize({
    en_GB: "How long ago did you quit?",
    sv_SE: "Hur längesen slutade du?",
  }),
};

const Snus = {
  Question: localize({
    en_GB: "Do you snus?",
    sv_SE: "Snusar du?",
  }),
  Yes: localize({
    en_GB: "Yes",
    sv_SE: "Ja",
  }),
  Quit: localize({
    en_GB: "No, I quit",
    sv_SE: "Nej, jag har slutat",
  }),
  No: localize({
    en_GB: "No",
    sv_SE: "Nej",
  }),
  HowOften: localize({
    en_GB: "How often do you snus?",
    sv_SE: "Hur ofta snusar du?",
  }),
  QuitSnusingRecency: localize({
    en_GB: "How long ago did you quit?",
    sv_SE: "Hur längesen slutade du?",
  }),
  SnusFrequencyAFewTimesAMonth: localize({
    en_GB: "A few times per month",
    sv_SE: "Några gånger per månad",
  }),
  SnusFrequencyOnWeekends: localize({
    en_GB: "On weekends",
    sv_SE: "Bara på helger",
  }),
  SnusFrequencyEveryOtherDay: localize({
    en_GB: "Every other day",
    sv_SE: "Varannan dag",
  }),
  SnusFrequencyEveryDay: localize({
    en_GB: "Every day",
    sv_SE: "Varje dag",
  }),
};

const Sunburn = {
  Question: localize({
    en_GB: "Have you been sunburnt in the past year?",
    sv_SE: "Har du bränt dig i solen senaste året?",
  }),
  No: localize({
    en_GB: "No",
    sv_SE: "Nej",
  }),
  FrequencyOneToTwo: localize({
    en_GB: "1-2 times",
    sv_SE: "1-2 gånger",
  }),
  FrequencyThreeToFive: localize({
    en_GB: "3-5 times",
    sv_SE: "3-5 gånger",
  }),
  FrequencySixToEight: localize({
    en_GB: "6-10 times",
    sv_SE: "6-10 gånger",
  }),
  FrequencyMoreThanTen: localize({
    en_GB: "More than 10 times",
    sv_SE: "Mer än 10 gånger",
  }),
};

const Cardiovascular = {
  Question: localize({
    en_GB: "Has anyone in your family had a cardiovascular disease?",
    // TODO: add SE
    sv_SE: "",
  }),
  Yes: localize({
    en_GB: "Yes",
    sv_SE: "Ja",
  }),
  No: localize({
    en_GB: "No",
    sv_SE: "Nej",
  }),
  Details: localize({
    en_GB: "Who and what type of cardiovascular disease?",
    // TODO: add SE
    sv_SE: "",
  }),
};

const SkinCancer = {
  Question: localize({
    en_GB: "Has anyone in your family had skin cancer?",
    // TODO: add SE
    sv_SE: "",
  }),
  Yes: localize({
    en_GB: "Yes",
    sv_SE: "Ja",
  }),
  No: localize({
    en_GB: "No",
    sv_SE: "Nej",
  }),
  NotSure: localize({
    en_GB: "I am not sure",
    // TODO: add SE
    sv_SE: "",
  }),
  MelanomaQuestion: localize({
    en_GB: "Was your relative diagnosed with melanoma?",
    // TODO: add SE
    sv_SE: "",
  }),
  MelanomaWhoQuestion: localize({
    en_GB: "Who was diagnosed with melanoma?",
    // TODO: add SE
    sv_SE: "",
  }),
  Child: localize({
    en_GB: "Child",
    // TODO: add SE
    sv_SE: "",
  }),
  Parent: localize({
    en_GB: "Parent",
    // TODO: add SE
    sv_SE: "",
  }),
  Sibling: localize({
    en_GB: "Sibling",
    // TODO: add SE
    sv_SE: "",
  }),
  OtherRelative: localize({
    en_GB: "Other relative",
    // TODO: add SE
    sv_SE: "",
  }),
};

const Diabetes = {
  Question: localize({
    en_GB: "Has anyone in your family had diabetes",
    // TODO: add SE
    sv_SE: "",
  }),
  Yes: localize({
    en_GB: "Yes",
    sv_SE: "Ja",
  }),
  No: localize({
    en_GB: "No",
    sv_SE: "Nej",
  }),
  Details: localize({
    en_GB: "Who and what type of diabetes?",
    // TODO: add SE
    sv_SE: "",
  }),
};

const RelevantConditions = {
  Question: localize({
    en_GB: "Does your family have a history of other conditions?",
    // TODO: add SE
    sv_SE: "",
  }),
  No: localize({
    en_GB: "No, none",
    // TODO: add SE
    sv_SE: "",
  }),
  Autoimmune: localize({
    en_GB: "Autoimmune disease",
    // TODO: add SE
    sv_SE: "",
  }),
  Cancer: localize({
    en_GB: "Cancer",
    // TODO: add SE
    sv_SE: "",
  }),
  Neurological: localize({
    en_GB: "Neurological disease",
    // TODO: add SE
    sv_SE: "",
  }),
  SomethingElse: localize({
    en_GB: "Something else",
    // TODO: add SE
    sv_SE: "",
  }),
  AutoimmuneDetails: localize({
    en_GB: "Who and which autoimmune disease?",
    // TODO: add SE
    sv_SE: "",
  }),
  CancerDetails: localize({
    en_GB: "Who and which cancer?",
    // TODO: add SE
    sv_SE: "",
  }),
  NeurologicalDetails: localize({
    en_GB: "Who and which neurological disease?",
    // TODO: add SE
    sv_SE: "",
  }),
  SomethingElseDetails: localize({
    en_GB: "Who and which health conditions?",
    // TODO: add SE
    sv_SE: "",
  }),
};

export const Trans = {
  AdditionalWorries,
  Allergy,
  Choice,
  Diet,
  Drinking,
  StrengthExercise,
  CardioExercise,
  FamilyConditions,
  Heritage,
  Medication,
  PersonalConditions,
  Smoking,
  Snus,
  WorldRegion,
  Sunburn,
  Cardiovascular,
  SkinCancer,
  Diabetes,
  RelevantConditions,
};
